import { useState, useCallback } from 'react';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import type { MatchingSetService } from '../../application/services/MatchingSetService';
import type { MatchingSet } from '../../domain/models/MatchingSet';

export interface UseMatchingSetReturn {
  matchingSet: MatchingSet | null;
  isLoading: boolean;
  sentinelRef: ReturnType<typeof useIntersectionObserver>['ref'];
}

type MatchingSetState = { designId: string; data: MatchingSet | null } | null;

export function useMatchingSet(
  service: MatchingSetService,
  designId: string
): UseMatchingSetReturn {
  const [matchingSetState, setMatchingSetState] = useState<MatchingSetState>(null);
  const [loadingDesignId, setLoadingDesignId] = useState<string | null>(null);
  const [fetchedDesignId, setFetchedDesignId] = useState<string | null>(null);

  const matchingSet = matchingSetState?.designId === designId ? matchingSetState.data : null;
  const isLoading = loadingDesignId === designId;
  const fetched = fetchedDesignId === designId;

  const handleIntersect = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (!entry.isIntersecting || fetched) return;

      const requestDesignId = designId;
      setFetchedDesignId(requestDesignId);
      setLoadingDesignId(requestDesignId);
      service
        .getMatchingSet(requestDesignId)
        .then((result) => {
          setMatchingSetState({ designId: requestDesignId, data: result });
        })
        .catch(() => {
          setMatchingSetState({ designId: requestDesignId, data: null });
        })
        .finally(() => {
          setLoadingDesignId((current) => (current === requestDesignId ? null : current));
        });
    },
    [fetched, service, designId]
  );

  const { ref: sentinelRef } = useIntersectionObserver(
    {
      rootMargin: '200px',
      threshold: 0,
      disconnectOnIntersect: true,
      enabled: !fetched,
    },
    handleIntersect
  );

  return { matchingSet, isLoading, sentinelRef };
}
